import * as React from 'react';

import { Chart, Emoji, ImageBlock, ImageInline, cn } from '@rematter/ui-web';
import {
  remarkChart,
  remarkDirectiveCleanup,
  remarkEmoji,
  remarkEntityReference,
  remarkImage,
} from '@rematter/ui-web/markdown-plugins';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';

import { AccountReference } from '../../../crm/_components/_assistant/AccountReference';
import { FacilityReference } from '../../../facilities/_components/_assistant/FacilityReference';
import { BillReference } from '../../../finance/_components/_assistant/BillReference';
import { InvoiceReference } from '../../../finance/_components/_assistant/InvoiceReference';
import { CategoryReference } from '../../../inventory/_components/_assistant/CategoryReference';
import { InventoryItemReference } from '../../../inventory/_components/_assistant/InventoryItemReference';
import { InventoryTagReference } from '../../../inventory/_components/_assistant/InventoryTagReference';
import { BookingReference } from '../../../trade/_components/_assistant/BookingReference';
import { LoadReference } from '../../../trade/_components/_assistant/LoadReference';
import { PurchaseOrderReference } from '../../../trade/_components/_assistant/PurchaseOrderReference';
import { SalesOrderReference } from '../../../trade/_components/_assistant/SalesOrderReference';
import { AssetReference } from '../../../transportation/_components/_assistant/AssetReference';
import { ContainerTypeReference } from '../../../transportation/_components/_assistant/ContainerTypeReference';
import { DriverReference } from '../../../transportation/_components/_assistant/DriverReference';
import { FreightForwarderReference } from '../../../transportation/_components/_assistant/FreightForwarderReference';
import { FreightLineReference } from '../../../transportation/_components/_assistant/FreightLineReference';
import { RouteReference } from '../../../transportation/_components/_assistant/RouteReference';
import { ShippingCarrierReference } from '../../../transportation/_components/_assistant/ShippingCarrierReference';
import { TaskReference } from '../../../transportation/_components/_assistant/TaskReference';
import { TrailerReference } from '../../../transportation/_components/_assistant/TrailerReference';
import { TruckReference } from '../../../transportation/_components/_assistant/TruckReference';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export const MarkdownContent = ({
  content,
  className,
}: MarkdownContentProps) => {
  const components = {
    // Entity reference components
    salesOrder: SalesOrderReference,
    booking: BookingReference,
    load: LoadReference,
    account: AccountReference,
    purchaseOrder: PurchaseOrderReference,
    invoice: InvoiceReference,
    bill: BillReference,
    inventoryItem: InventoryItemReference,
    facility: FacilityReference,
    category: CategoryReference,
    inventoryTag: InventoryTagReference,
    driver: DriverReference,
    truck: TruckReference,
    trailer: TrailerReference,
    containerType: ContainerTypeReference,
    shippingCarrier: ShippingCarrierReference,
    freightForwarder: FreightForwarderReference,
    freightLine: FreightLineReference,
    route: RouteReference,
    asset: AssetReference,
    task: TaskReference,

    // Assistant components
    emoji: Emoji,
    chart: Chart,
    imageBlock: ImageBlock,
    imageInline: ImageInline,

    // Headings
    h1: ({ children }) => (
      <h1 className="mt-6 mb-4 text-2xl font-bold">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="mt-5 mb-3 text-xl font-bold">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-4 mb-2 text-lg font-semibold">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="mt-3 mb-2 text-base font-semibold">{children}</h4>
    ),
    h5: ({ children }) => (
      <h5 className="mt-2 mb-1 text-sm font-semibold">{children}</h5>
    ),
    h6: ({ children }) => (
      <h6 className="mt-2 mb-1 text-xs font-semibold">{children}</h6>
    ),

    // Paragraphs with safeguard for block-level children
    p: ({ children }) => {
      // Recursively check if children contain any of our custom block-like components
      // by inspecting React elements for specific className patterns at any depth
      const hasBlockLikeChildren = (nodes: React.ReactNode): boolean =>
        React.Children.toArray(nodes).some((child) => {
          if (React.isValidElement(child)) {
            const props = child.props as {
              className?: string;
              children?: React.ReactNode;
            };
            const className = props?.className || '';
            // Check for our inline-block wrapped components
            if (
              typeof className === 'string' &&
              (className.includes('@ChartWrapper') ||
                className.includes('@ImageBlock') ||
                className.includes('inline-block w-full'))
            ) {
              return true;
            }
            // Recursively check nested children
            if (props?.children) {
              return hasBlockLikeChildren(props.children);
            }
          }
          return false;
        });

      // Use div for block-like children to avoid hydration errors
      if (hasBlockLikeChildren(children)) {
        return <div className="mb-4 leading-7">{children}</div>;
      }

      return <p className="mb-4 leading-7">{children}</p>;
    },

    // Lists
    ul: ({ children }) => (
      <ul className="mb-4 ml-6 list-disc space-y-2">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-4 ml-6 list-decimal space-y-2">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-7">{children}</li>,

    // Code blocks and inline code
    code: ({ className, children, ...props }) => {
      const isInline = !className;
      if (isInline) {
        return (
          <code
            className="rounded bg-neutral/20 px-1.5 py-0.5 font-mono text-sm"
            {...props}
          >
            {children}
          </code>
        );
      }
      return (
        <code
          className={cn(
            'block overflow-x-auto rounded-lg bg-neutral/10 p-4 font-mono text-sm',
            className,
          )}
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="mb-4 overflow-x-auto">{children}</pre>
    ),

    // Blockquotes
    blockquote: ({ children }) => (
      <blockquote className="mb-4 border-l-4 border-neutral/30 pl-4 text-neutral/80 italic">
        {children}
      </blockquote>
    ),

    // Links
    a: ({ children, href }) => (
      <a
        href={href}
        className="text-primary underline hover:text-primary/80"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),

    // Horizontal rule
    hr: () => <hr className="my-6 border-neutral/20" />,

    // Tables
    table: ({ children }) => (
      <div className="mb-4 overflow-x-auto">
        <table className="min-w-full border-collapse border border-neutral/20">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-neutral/10">{children}</thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr className="border-b border-neutral/20">{children}</tr>
    ),
    th: ({ children }) => (
      <th className="border border-neutral/20 px-4 py-2 text-left font-semibold">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-neutral/20 px-4 py-2">{children}</td>
    ),

    // Images
    img: ({ src, alt }) => (
      <img
        src={src}
        alt={alt}
        className="mb-4 max-w-full rounded-lg"
        loading="lazy"
      />
    ),

    // Strong and emphasis
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,

    // Strikethrough (from GFM)
    del: ({ children }) => <del className="line-through">{children}</del>,
  } satisfies Partial<Components> & Record<string, React.ElementType>;

  return (
    <ReactMarkdown
      className={cn(
        '@MarkdownContent',
        'prose prose-sm max-w-none break-words',
        className,
      )}
      remarkPlugins={[
        remarkGfm,
        remarkDirective,
        remarkEntityReference,
        remarkEmoji,
        remarkChart,
        remarkImage,
        remarkDirectiveCleanup, // Must be last to clean up unhandled directives
      ]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
};
